/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { httpService } from 'src/core/services/api/httpService';
import { luisRequestOptionsBuilder } from 'src/core/utils/api/luisRequestOptionsBuilder';
import { urlBuilder } from 'src/core/utils/api/urlBuilder';
import { createService } from 'src/core/utils/createService';
import { FeatureSwagger } from 'src/luis/modules/features/types/FeatureSwagger';
import { Model } from 'src/luis/types/Model';
import { ModelType } from 'src/luis/types/ModelType';

export type ModelIdentifier = { model: Omit<Model, 'name'> };

/**
 * Represents a features service.
 */
export const getLuisFeatureService = createService(({ context, getEnvironment }) => {
    const { apiUrl, isProduction } = getEnvironment({
        apiVersion: 'v3.0-preview'
    });
    const apiOptions = luisRequestOptionsBuilder()
        .useDefaultOptions(context.serviceKey, isProduction)
        .build();

    /**
     * Gets the url to use for requests.
     *
     * @param model Model id and typeId of the app to fetch the features from
     * make the call for.
     */
    const getRequestUrl = (model: Omit<Model, 'name'>): string =>
        urlBuilder()
            .baseUrl(apiUrl)
            .app(context.appId)
            .version(context.versionId)
            .resource(`${model.typeId === ModelType.Intent ? 'intents' : 'entities'}`, `${model.id}`)
            .resource('features')
            .build();

    /**
     * Gets the features for the given model.
     *
     * @param param0.model Model id and typeId of the app to fetch the features from
     * @returns A list of features.
     */
    const getFeatures = async ({ model }: { model: Omit<Model, 'name'> }): Promise<FeatureSwagger[]> => {
        const { data: features } = await httpService.get<FeatureSwagger[]>(getRequestUrl(model), apiOptions);

        return features;
    };

    /**
     * Adds feature for the given model.
     *
     * @param param0.model Model id and typeId of the app to fetch the features from
     * @param param0.feature The feature to be added
     */
    const addFeature = async ({ model, feature }: { model: Omit<Model, 'name'>; feature: FeatureSwagger }): Promise<void> => {
        await httpService.post<void>(getRequestUrl(model), feature, apiOptions);
    };

    /**
     * Updates the features of the given model.
     *
     * @param param0.model Model id and typeId of the app to
     * fetch the features from
     * @param param0.features The features to be updated
     */
    const updateFeatures = async ({
        model,
        features
    }: {
        model: Pick<Model, 'id' | 'typeId'>;
        features: FeatureSwagger[];
    }): Promise<void> => {
        await httpService.put<void>(getRequestUrl(model), features, apiOptions);
    };

    /**
     * Deletes the feature from the given model.
     *
     * @param param0.model Model id and typeId of the app to fetch the feature from
     * @param param0.feature The feature to be deleted
     */
    const deleteFeature = async ({ model, feature }: { model: Omit<Model, 'name'>; feature: FeatureSwagger }): Promise<void> => {
        await httpService.delete<void>(getRequestUrl(model), { ...apiOptions, data: feature });
    };

    return { getFeatures, addFeature, updateFeatures, deleteFeature };
});
